'use client'

import { useSyncExternalStore } from 'react'

export type PwaLaunchSource = 'hub' | 'app' | null

const STORAGE_KEY = 'pwa_launch_source'
let inferred = false

function inferOnce() {
  if (inferred) return
  inferred = true
  if (typeof window === 'undefined') return
  try {
    const existing = sessionStorage.getItem(STORAGE_KEY)
    if (existing === 'hub' || existing === 'app') return
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true
    if (!isStandalone) return
    const value: 'hub' | 'app' = window.location.pathname.startsWith('/apps/')
      ? 'app'
      : 'hub'
    sessionStorage.setItem(STORAGE_KEY, value)
  } catch {
    // sessionStorage / matchMedia unavailable — leave source as null.
  }
}

function subscribe() {
  // sessionStorage doesn't emit events to the same tab, but inference runs
  // exactly once per page so there's nothing to subscribe to.
  return () => {}
}

function readSource(): PwaLaunchSource {
  if (typeof window === 'undefined') return null
  inferOnce()
  try {
    const value = sessionStorage.getItem(STORAGE_KEY)
    return value === 'hub' || value === 'app' ? value : null
  } catch {
    return null
  }
}

function readServerSource(): PwaLaunchSource {
  return null
}

/**
 * Detects how the current PWA session was launched.
 * - 'hub'  → launched from the main manifest (scope /dashboard, start_url /dashboard)
 * - 'app'  → launched from a per-app manifest (scope /apps/{id})
 * - null   → not a standalone PWA launch (regular browser tab)
 *
 * On first client read, infers the source from display-mode + pathname when
 * sessionStorage doesn't already hold one. Per-app PWAs can't redirect
 * through a /launch route because their scope confines them.
 */
export function usePwaLaunchSource(): PwaLaunchSource {
  return useSyncExternalStore(subscribe, readSource, readServerSource)
}
